import { AxiosError } from 'axios';
import { api } from '@shared/api';
import { mapSenseContexts } from '@shared/lib';
import type { Language, Level, ModuleState, ModuleSummary, Word, WordMeaning } from '@shared/types';

export type CreateModuleErrorCode =
  | 'DUPLICATE_NAME'
  | 'RESTRICTED'
  /** 403 EMAIL_NOT_VERIFIED: создавать контент можно после подтверждения почты. */
  | 'EMAIL_NOT_VERIFIED'
  /** 429: лимит создания контента в минуту. */
  | 'RATE_LIMIT'
  /** 5xx: сервер не справился. */
  | 'SERVER'
  | 'NETWORK'
  | 'UNKNOWN';
/** Поле модуля, не прошедшее модерацию (бэк: MODULE_TEXT_RESTRICTED, details.fields). */
export type CreateModuleField = 'name' | 'description';

export class CreateModuleError extends Error {
  constructor(
    public code: CreateModuleErrorCode,
    public fields: CreateModuleField[] = [],
  ) {
    super(code);
    this.name = 'CreateModuleError';
  }
}

interface BackendTranslation {
  id: string;
  text: string;
  gender: string | null;
}

interface BackendSense {
  id: string;
  translations: BackendTranslation[];
  en_synonyms: string[];
  example_en: string | null;
  simple_example_en?: string | null;
  hint: string | null;
  selection_options: string[] | null;
  is_rare?: boolean;
  contexts?: unknown;
}

interface BackendMeaning {
  id: string;
  pos: string | null;
  senses: BackendSense[];
}

interface BackendCard {
  id: string;
  front_name: string;
  primary_translation: string | null;
  transcription: string | null;
  cefr: Level | null;
  meanings: BackendMeaning[];
}

function mapMeanings(meanings: BackendMeaning[] | null | undefined): WordMeaning[] {
  if (!Array.isArray(meanings)) return [];
  return meanings.map((m) => ({
    id: m.id,
    pos: m.pos ?? null,
    senses: (m.senses ?? []).map((s) => ({
      id: s.id,
      translations: (s.translations ?? []).map((t) => ({
        id: t.id,
        text: t.text,
        gender: t.gender ?? null,
      })),
      enSynonyms: s.en_synonyms ?? [],
      exampleEn: s.example_en ?? null,
      simpleExampleEn: s.simple_example_en ?? null,
      hint: s.hint ?? null,
      selectionOptions: s.selection_options ?? null,
      isRare: s.is_rare ?? false,
      contexts: mapSenseContexts(s.contexts),
    })),
  }));
}

function pickPrimary(card: BackendCard): string {
  if (card.primary_translation?.trim()) return card.primary_translation.trim();
  return card.meanings?.[0]?.senses?.[0]?.translations?.[0]?.text?.trim() ?? '';
}

interface BackendCardLink {
  module_id: string;
  card_id: string;
  card: BackendCard;
}

interface BackendUserModule {
  id: string;
  name: string;
  description: string | null;
  cefr_level: Level | null;
  source_lang: Language;
  target_lang: Language;
  is_public: boolean;
  status: 'active' | 'paused' | 'deleted';
  is_seed: boolean;
  card_links?: BackendCardLink[];
  /** Только в GET /modules: карточки без удалённых. card_links после удаления остаются. */
  card_count?: number | null;
}

function deriveState(words: number, status: BackendUserModule['status']): ModuleState {
  if (words === 0) return 'new';
  if (status === 'paused') return 'progress';
  return 'progress';
}

function cardFromLink(link: BackendCardLink): Word {
  return {
    word: link.card.front_name,
    ipa: link.card.transcription ?? '',
    ru: pickPrimary(link.card),
    state: 'new',
    meanings: mapMeanings(link.card.meanings),
  };
}

function mapBackendModule(m: BackendUserModule): ModuleSummary {
  const cards = (m.card_links ?? []).map(cardFromLink);
  const words = m.card_count ?? cards.length;
  // Связь — на значение: у слова с двумя значениями их две (cluster · cluster).
  const preview = [...new Set(cards.map((c) => c.word))].slice(0, 6);
  return {
    id: m.id,
    title: m.name,
    desc: m.description?.trim() || 'Свой модуль',
    words,
    due: 0,
    fresh: words,
    progress: 0,
    state: deriveState(words, m.status),
    active: m.status === 'active',
    deleted: m.status === 'deleted',
    isPublic: m.is_public,
    preview,
    cards,
    // Бэк гарантирует source_lang/target_lang после миграции 078584e238d9 (NOT NULL).
    // Фолбэк en→ru — на случай, если фронт встретится со старым ответом до рестарта.
    sourceLang: m.source_lang ?? 'en',
    targetLang: m.target_lang ?? 'ru',
  };
}

export type UserModuleStatus = 'active' | 'paused' | 'deleted' | 'available';

export interface UserModulesQueryArgs {
  page?: number;
  size?: number;
  search?: string;
  status?: UserModuleStatus;
}

export interface UserModulesPage {
  items: ModuleSummary[];
  total: number;
  page: number;
  pageSize: number;
  pages: number;
}

interface BackendPage<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export async function fetchUserModulesPage(
  args: UserModulesQueryArgs = {},
): Promise<UserModulesPage> {
  const params: Record<string, string | number | boolean> = {
    page: args.page ?? 1,
    size: args.size ?? 20,
  };
  const search = args.search?.trim();
  if (search) params.search = search;
  if (args.status) params.status = args.status;

  const { data } = await api.get<BackendPage<BackendUserModule>>('/modules', { params });
  return {
    items: data.items.map(mapBackendModule),
    total: data.total,
    page: data.page,
    pageSize: data.size,
    pages: data.pages,
  };
}

export async function fetchModuleById(id: string): Promise<ModuleSummary | null> {
  // Модуль может находиться дальше первой страницы, в том числе после выбора
  // через поиск в тренировке. Ищем в полном списке, включая удалённые для restore.
  let pageNumber = 1;
  let pages = 1;
  do {
    const page = await fetchUserModulesPage({ page: pageNumber, size: 100 });
    const found = page.items.find((m) => m.id === id);
    if (found) return found;
    pages = page.pages;
    pageNumber += 1;
  } while (pageNumber <= pages);
  return null;
}

export async function pauseModule(id: string): Promise<void> {
  await api.put(`/modules/pause/${id}`);
}

export async function activateModule(id: string): Promise<void> {
  await api.put(`/modules/active/${id}`);
}

export async function makeModulePublic(id: string): Promise<void> {
  await api.put(`/modules/${id}/visibility`, { is_public: true });
}

export async function makeModulePrivate(id: string): Promise<void> {
  await api.put(`/modules/${id}/visibility`, { is_public: false });
}

export interface CreateModulePayload {
  name: string;
  desc?: string;
  cefrLevel: Level;
  sourceLang: Language;
  targetLang: Language;
}

export async function createModule(payload: CreateModulePayload): Promise<ModuleSummary> {
  const name = payload.name.trim();
  if (!name) throw new CreateModuleError('UNKNOWN');
  let data: BackendUserModule;
  try {
    ({ data } = await api.post<BackendUserModule>('/modules/create', {
      name,
      description: payload.desc?.trim() || null,
      cefr_level: payload.cefrLevel,
      source_lang: payload.sourceLang,
      target_lang: payload.targetLang,
    }));
  } catch (err) {
    if (err instanceof AxiosError) {
      const code = err.response?.data?.error?.code;
      if (code === 'DUPLICATE_MODULE_NAME') throw new CreateModuleError('DUPLICATE_NAME');
      if (code === 'MODULE_TEXT_RESTRICTED') {
        const fields = err.response?.data?.error?.details?.fields;
        throw new CreateModuleError('RESTRICTED', Array.isArray(fields) ? fields : []);
      }
      if (code === 'EMAIL_NOT_VERIFIED') throw new CreateModuleError('EMAIL_NOT_VERIFIED');
      if (!err.response) throw new CreateModuleError('NETWORK');
      if (err.response.status === 429) throw new CreateModuleError('RATE_LIMIT');
      if (err.response.status >= 500) throw new CreateModuleError('SERVER');
    }
    throw new CreateModuleError('UNKNOWN');
  }
  // Модуль уже создан: ошибка разбора ответа не должна выглядеть как «не удалось создать».
  return mapBackendModule(data);
}

export async function deleteModule(id: string): Promise<void> {
  await api.delete(`/modules/delete/${id}`);
}

export async function restoreModule(id: string): Promise<void> {
  await activateModule(id);
}
